import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, BoxProps, Button, Center, Stack, Text } from '@mantine/core';

interface ViewerProps extends BoxProps {
  assetUrl: string;
  thumbnail?: string;
  customisationJson: string;
  height: number;
  isLarge: boolean;
}

interface Viewer {
  setLoadingScreenPlaceholder: (thumbnail?: string) => Promise<void>;
  loadAsset: (thumbnail: string, assetUrl: string) => Promise<void>;
  loadCustomizationWithJSON: (customisationJson: string) => Promise<void>;
}

export function isTooLargeToAutoLoad3DView(size: number) {
  const maxModelSizeToAutoLoadViewer = 50 * 1024 * 1024; //50MB
  return size > maxModelSizeToAutoLoadViewer;
}

export function Viewer({ assetUrl, thumbnail, customisationJson, height, isLarge, ...props }: ViewerProps) {
  const viewerRef = useRef<HTMLIFrameElement>(null);
  const [shouldLoad, setShouldLoad] = useState(!isLarge);

  const onLoad = useCallback(async () => {
    if (!viewerRef.current) {
      return;
    }
    const content: ((Window | Document) & { viewer?: Viewer }) | null =
      viewerRef.current!.contentWindow || viewerRef.current!.contentDocument;

    if (!content?.viewer) {
      return;
    }

    if (thumbnail) {
      await content.viewer.setLoadingScreenPlaceholder(thumbnail);
    }
    await content.viewer.loadAsset('', assetUrl);
    await content.viewer.loadCustomizationWithJSON(customisationJson);
  }, [assetUrl, customisationJson, thumbnail]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <Box pos='relative' {...props}>
      {shouldLoad ? (
        <iframe
          id='pbrPreview'
          frameBorder='0'
          allowFullScreen
          ref={viewerRef}
          src='/viewer/index.html'
          onLoad={onLoad}
          width='100%'
          height={height}
        />
      ) : (
        <Center h={height}>
          <Stack>
            <Text ta='center'>
              This is a large file that can impact your browser&apos;s performance. Do you want to continue loading it?
            </Text>
            <Button variant='light' onClick={() => setShouldLoad(true)}>
              Load 3D Model
            </Button>
          </Stack>
        </Center>
      )}
    </Box>
  );
}
